// MagnetboardView — Nächster-Dienst-Ansicht (Kladden-Stil) mit Fahrzeug-Karten
import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Truck, Sun, Moon, UserPlus } from 'lucide-react';
import { dienstApi } from '../../lib/api';
import { formatDate, cn, qualifikationLabels } from '../../lib/utils';
import { DIENSTGRAD_CONFIG } from '../../lib/dienstgrade';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import EmployeePhoto from '../mitarbeiter/EmployeePhoto';
import {
  extractFahrzeugNummer,
  vehicleImages,
  getFahrzeugspezifischeAbkuerzung,
  berechneFahrzeugNummerInTyp,
  getFahrzeugFarbe,
  getKontrastFarbe,
} from './constants';
import {
  sortMagnetboardFunktionen,
  getMitfahrerImFahrzeug,
  formatMitfahrerZusatz,
  getMagnetboardIststaerke,
} from './magnetboardLayout';

export function MagnetboardView({ wacheId, abteilungId, boardData, boardFahrzeuge }) {
  const [loadedData, setData] = useState(null);
  const data = boardData || loadedData;
  const [isLoading, setIsLoading] = useState(true);
  const [loadedFahrzeuge, setFahrzeuge] = useState([]);
  const fahrzeuge = boardFahrzeuge || loadedFahrzeuge;
  // 'next' = Nächster Dienst (Standard), 'today' = Heute
  const [modus, setModus] = useState('next');

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = { abteilung_id: abteilungId };
      if (modus === 'today') params.nur_heute = true;
      const [nextRes, fzRes] = await Promise.all([
        dienstApi.getNaechsterDienst(params),
        dienstApi.getFahrzeuge(),
      ]);
      setData(nextRes.data);
      setFahrzeuge(fzRes.data);
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [abteilungId, modus]);

  useEffect(() => {
    if (!boardData && abteilungId) loadData();
    else if (!boardData) setIsLoading(false);
  }, [abteilungId, modus, loadData, boardData]);

  // Toggle-Komponente — wird über und unter den Lade-/Empty-States gerendert
  const ModusToggle = !boardData && (
    <div
      className="inline-flex rounded-lg border bg-muted/50 p-0.5 text-xs font-medium"
      data-testid="magnetboard-modus-toggle"
    >
      <button
        type="button"
        onClick={() => setModus('next')}
        className={cn(
          'px-3 py-1.5 rounded-md transition-colors',
          modus === 'next'
            ? 'bg-background shadow-sm text-foreground'
            : 'text-muted-foreground hover:text-foreground'
        )}
        data-testid="modus-next-btn"
      >
        Nächster Dienst
      </button>
      <button
        type="button"
        onClick={() => setModus('today')}
        className={cn(
          'px-3 py-1.5 rounded-md transition-colors',
          modus === 'today'
            ? 'bg-background shadow-sm text-foreground'
            : 'text-muted-foreground hover:text-foreground'
        )}
        data-testid="modus-today-btn"
      >
        Heute
      </button>
    </div>
  );

  if (!boardData && isLoading) {
    return (
      <div className="space-y-3">
        {ModusToggle}
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!data?.datum) {
    return (
      <div className="space-y-3">
        {ModusToggle}
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-medium">
              {modus === 'today' ? 'Heute kein Dienst' : 'Keine Einteilung gefunden'}
            </p>
            <p className="text-muted-foreground">
              {modus === 'today'
                ? 'Für die aktuelle Abteilung ist heute kein Dienst geplant.'
                : 'Für die nächsten Tage ist noch keine Einteilung hinterlegt.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { datum, dienst_art, einteilungen_nach_fahrzeug, mein_dienst, anwesend_ohne_funktion } = data;
  // Reihenfolge:
  //  1) Trägerfahrzeuge (LHF/HLF/TLF) mit Sollstärke > 0
  //  2) Restliche eingeteilte Fahrzeuge nach `sortierung`-Feld
  //  3) Anwesende ohne Funktion
  //  4) Unbesetzte optionale Fahrzeuge ganz unten
  const typPrio = (fz) => {
    const t = (fz.fahrzeugtyp_code || fz.funkrufname || '').toUpperCase();
    if (t.startsWith('LHF') || t.startsWith('HLF') || t.startsWith('TLF')) return 0;
    return 1;
  };
  const getSollstaerke = (fz) => (dienst_art === 'T' ? (fz.staerke_tag ?? 2) : (fz.staerke_nacht ?? 2));
  const getIststaerke = (fz) => getMagnetboardIststaerke(
    einteilungen_nach_fahrzeug?.[fz.funkrufname] || []
  );
  const istUnbesetztesOptionalesFahrzeug = (fz) =>
    getSollstaerke(fz) === 0 && getIststaerke(fz) === 0;
  const sortedFahrzeuge = fahrzeuge
    .filter(fz => !fz.ist_springer && fz.dienstzeit_modell !== 'SPRINGER')
    .sort((a, b) => {
      // Nur unbesetzte optionale Fahrzeuge kommen ganz nach unten.
      const sa = istUnbesetztesOptionalesFahrzeug(a) ? 1 : 0;
      const sb = istUnbesetztesOptionalesFahrzeug(b) ? 1 : 0;
      if (!boardData && sa !== sb) return sa - sb;
      const pa = typPrio(a);
      const pb = typPrio(b);
      if (pa !== pb) return pa - pb;
      return (a.sortierung || 0) - (b.sortierung || 0);
    });

  const fahrzeugNummerMap = {};
  sortedFahrzeuge.forEach(fz => {
    // Typ-basierte Nummerierung: nur Zahl, wenn mehrere Fahrzeuge gleichen Typs
    fahrzeugNummerMap[fz.id] = berechneFahrzeugNummerInTyp(sortedFahrzeuge, fz);
  });

  return (
    <div className="space-y-3">
      {ModusToggle}

      {/* Meine Einteilung — kompakt, über dem Magnetboard */}
      {!boardData && mein_dienst && (
        <div
          className="px-3 py-2 bg-primary/10 rounded-lg border border-primary/30 flex items-baseline gap-2"
          data-testid="mein-dienst-info"
        >
          <p className="text-[11px] text-muted-foreground shrink-0">Du:</p>
          <p className="font-bold text-sm text-primary truncate">
            {mein_dienst.fahrzeug} • {mein_dienst.funktion}
          </p>
        </div>
      )}

      {/* Einheits-Board: Header + Fahrzeuge + Anwesend ohne Funktion nahtlos in einem Container */}
      <div className="rounded-lg overflow-hidden border shadow-sm">
        {/* Kompakter Header — ohne linke Datums-Box (Datum steht bereits rechts im Titel) */}
        <div className="flex items-center justify-between gap-3 px-3 py-2 bg-gradient-to-r from-slate-800 to-slate-700 text-white">
          <div className="min-w-0">
            <p className="text-[10px] opacity-70 leading-none">
              {boardData ? (data.ist_aktuell ? 'Aktuell im Dienst' : 'Kommender Dienst') : modus === 'today' ? 'Heutiger Dienst' : 'Nächster Dienst'}
            </p>
            <h2 className="text-sm font-bold truncate">
              {formatDate(datum, { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
            </h2>
          </div>
          <Badge
            variant="outline"
            className={cn(
              'shrink-0 text-xs px-2.5 py-1 border-2 font-bold',
              dienst_art === 'T'
                ? 'bg-amber-500/20 border-amber-400 text-amber-100'
                : 'bg-indigo-500/20 border-indigo-400 text-indigo-100'
            )}
          >
            {dienst_art === 'T'
              ? (<><Sun className="w-3.5 h-3.5 mr-1" /> TAG</>)
              : (<><Moon className="w-3.5 h-3.5 mr-1" /> NACHT</>)}
          </Badge>
        </div>

        {/* Fahrzeug-Karten — nahtlos aneinander wie ein echtes Magnetboard */}
        <div className="flex flex-col gap-px bg-border">
        {sortedFahrzeuge.map((fz) => {
          const einteilungen = einteilungen_nach_fahrzeug?.[fz.funkrufname] || [];
          const funktionen = fz.funktionen || [];
          const typCode = (fz.fahrzeugtyp_code || '').match(/^([A-Za-z]+)/)?.[1]?.toUpperCase() || 'LHF';
          const imageUrl = vehicleImages[typCode] || vehicleImages.LHF;
          const fzNummer = fahrzeugNummerMap[fz.id] || '';

          const sollstaerke = dienst_art === 'T' ? (fz.staerke_tag ?? 2) : (fz.staerke_nacht ?? 2);
          // Iststärke: Gäste MIT hinterlegter Aushilfe-Person zählen wie normale
          // Einteilungen — nur wirklich unbesetzte Gast-Slots (kein Mitarbeiter,
          // kein Aushilfe-User) werden ausgeschlossen.
          const iststaerke = getIststaerke(fz);
          const differenz = iststaerke - sollstaerke;
          const istVoll = differenz >= 0;
          // Fahrzeug mit Sollstärke 0 (z. B. DLK) → dezent grau darstellen,
          // aber weiterhin voll einteilbar (Zusatzpersonal möglich).
          const keineSollstaerke = sollstaerke === 0;

          // Feste Reihenfolge bei RTWs:
          //  links: MVE (Beifahrer / Sanitäter)
          //  rechts: Maschinist (RTW-MA, Ma, RTW-Fa, …)
          // Andere Kürzel behalten ihre originale Reihenfolge dazwischen (selten).
          const sortedFunktionen = sortMagnetboardFunktionen(funktionen, typCode);
          const mitfahrerOhneSitzfunktion = getMitfahrerImFahrzeug(einteilungen);
          const mitfahrerZusatz = formatMitfahrerZusatz(mitfahrerOhneSitzfunktion);

          return (
            <div key={fz.id} className={cn(
              'overflow-hidden bg-white dark:bg-slate-900',
              istUnbesetztesOptionalesFahrzeug(fz) ? 'order-3' : 'order-1',
              keineSollstaerke && 'opacity-70'
            )}>
              {/* Fahrzeug-Header — kompakte Zeile */}
              <div className={cn(
                'text-white px-2.5 py-1.5 flex items-center gap-2',
                keineSollstaerke ? 'bg-slate-500' : 'bg-slate-700'
              )}>
                <div className="w-8 h-6 rounded bg-white/10 flex items-center justify-center overflow-hidden shrink-0">
                  <img
                    src={imageUrl}
                    alt={typCode}
                    className="max-h-full max-w-full object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
                <h3
                  className="font-bold text-xs truncate flex-1"
                  title={[fz.funkrufname, mitfahrerZusatz].filter(Boolean).join(' ')}
                >
                  {fz.funkrufname}
                  {mitfahrerZusatz && (
                    <span className="ml-1 font-semibold text-amber-200">{mitfahrerZusatz}</span>
                  )}
                </h3>
                {mitfahrerOhneSitzfunktion.some(person => person.avatar_url) && (
                  <div className="flex -space-x-1">
                    {mitfahrerOhneSitzfunktion.map(person => (
                      <EmployeePhoto key={person.mitarbeiter_id} src={person.avatar_url}
                        name={person.aushilfe_user_name || person.mitarbeiter_name} className="h-6 w-5 ring-1 ring-white" />
                    ))}
                  </div>
                )}
                <div className={cn(
                  'px-1.5 py-0.5 rounded text-[11px] font-bold shrink-0',
                  keineSollstaerke
                    ? 'bg-white/10 text-slate-200'
                    : istVoll ? 'bg-emerald-500/30 text-emerald-200' : 'bg-red-500/30 text-red-200'
                )}>
                  {keineSollstaerke ? (
                    <>
                      {iststaerke > 0 ? iststaerke : '–'}
                      <span className="opacity-70 ml-1">optional</span>
                    </>
                  ) : (
                    <>
                      {iststaerke}/{sollstaerke}
                      {differenz !== 0 && (
                        <span className={cn('ml-1', differenz > 0 ? 'text-emerald-300' : 'text-red-300')}>
                          ({differenz > 0 ? `+${differenz}` : differenz})
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Funktionen — 2-Spalten-Grid (Trupp-Anordnung), Magnetboard-Stil */}
              <div className="grid grid-cols-2 gap-px bg-border">
                {sortedFunktionen.length > 0 ? sortedFunktionen.map((fn, i) => {
                  const besetzung = einteilungen.find(e => e.funktion_kuerzel === fn.kuerzel);
                  const fzFarbe = getFahrzeugFarbe(fz);
                  const fzTextFarbe = getKontrastFarbe(fzFarbe);
                  const abkuerzungMitNummer = getFahrzeugspezifischeAbkuerzung(fn.kuerzel, fzNummer);

                  // Magnetboard-Daten der besetzten Person
                  const dgCfg = besetzung?.dienstgrad
                    ? DIENSTGRAD_CONFIG[besetzung.dienstgrad.toUpperCase()]
                    : null;
                  const quali = besetzung?.qualifikation; // "RS", "NotSan", ...
                  // Wenn Aushilfe: echten Namen anzeigen statt „Gast X"
                  const gastFallback = besetzung?.mitarbeiter_id?.match(/^__gast_(\d+)__$/);
                  const displayName = besetzung?.aushilfe_user_name
                    || besetzung?.mitarbeiter_name
                    || (gastFallback ? `Gast ${gastFallback[1]}` : '');
                  const nameParts = displayName.split(' ');
                  const vorname = nameParts.slice(0, -1).join(' ');
                  const nachname = nameParts.slice(-1)[0] || '';

                  return (
                    <div
                      key={i}
                      className={cn('bg-white dark:bg-slate-900 flex items-stretch', boardData ? 'min-h-[48px]' : 'min-h-[44px]')}
                    >
                      {/* Funktions-Kürzel */}
                      <div
                        className="shrink-0 w-12 px-1.5 flex items-center justify-center text-center text-[10px] font-bold tracking-wide leading-tight border-r border-black/10"
                        style={{ backgroundColor: fzFarbe, color: fzTextFarbe }}
                      >
                        {abkuerzungMitNummer}
                      </div>
                      <EmployeePhoto src={besetzung?.avatar_url} name={displayName} fullHeight />

                      {/* Personen-Daten */}
                      <div className="flex-1 min-w-0 px-1.5 py-1 flex items-center gap-1.5">
                        {besetzung ? (
                          <>
                            {/* Schulterstück (Dienstgrad-Insignie) — kompakt hochkant, Streifen oben */}
                            {dgCfg?.image && (
                              <div className="w-4 h-10 shrink-0 flex items-center justify-center">
                                <img
                                  src={dgCfg.image}
                                  alt={besetzung.dienstgrad}
                                  title={dgCfg.label}
                                  className="block max-w-none origin-center rotate-90"
                                  style={{ width: '40px', height: '14px' }}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              </div>
                            )}

                            {/* Name (Vorname dünn, Nachname fett) + Springer */}
                            <div className="flex-1 min-w-0 leading-tight" title={displayName}>
                              {vorname && (
                                <div className="text-[10px] text-muted-foreground truncate">
                                  {vorname}
                                </div>
                              )}
                              <div className="text-xs font-bold text-foreground truncate">
                                {nachname}
                              </div>
                              {besetzung.aushilfe_user_name && (
                                <div className="text-[9px] text-violet-700 truncate">
                                  Aushilfe{besetzung.aushilfe_abteilung_name ? ` · ${besetzung.aushilfe_abteilung_name}` : ''}
                                </div>
                              )}
                              {/* Springer-Funktionen (z.B. +RTW6, +DLK) */}
                              {besetzung.sprungfunktionen && besetzung.sprungfunktionen.length > 0 && (
                                <div className="flex flex-wrap gap-0.5 mt-0.5">
                                  {besetzung.sprungfunktionen.map((sf, idx) => {
                                    const sfFzNummer = sf.fahrzeug_id ? (fahrzeugNummerMap[sf.fahrzeug_id] || '') : '';
                                    const sfTyp = (sf.fahrzeug || '').split(' ')[0] || '';
                                    const sfLabel = sf.kuerzel
                                      ? getFahrzeugspezifischeAbkuerzung(sf.kuerzel, sfFzNummer)
                                      : `${sfTyp}${sfFzNummer}`;
                                    return (
                                      <span
                                        key={idx}
                                        className="inline-flex items-center px-1 py-px rounded bg-amber-100 text-amber-800 border border-amber-300 text-[9px] font-bold leading-tight"
                                        title={`Springer: ${sf.name || ''} (${sf.fahrzeug || ''})`}
                                      >
                                        +{sfLabel}
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </div>

                            {/* Qualifikations-Pill (RS / NotSan) — nur wenn vorhanden (Praktikanten haben keine) */}
                            {quali && quali !== 'NONE' && (
                              <span
                                className={cn(
                                  'shrink-0 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase',
                                  quali === 'NotSan'
                                    ? 'bg-emerald-600 text-white'
                                    : ['RS', 'RS+', 'RSN'].includes(quali)
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-slate-500 text-white'
                                )}
                                title={qualifikationLabels[quali] || quali}
                              >
                                {quali === 'NotSan' ? 'NFS' : quali}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-red-500 italic">— frei —</span>
                        )}
                      </div>
                    </div>
                  );
                }) : (
                  <div className="col-span-2 text-center text-muted-foreground text-xs py-3">
                    Keine Funktionen definiert
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Anwesend ohne Funktion — ZBV, WA, RW als virtuelle Fahrzeug-Karte
            im gleichen Magnetboard-Container, damit die Optik durchgängig bleibt. */}
        {Array.isArray(anwesend_ohne_funktion) && anwesend_ohne_funktion.length > 0 && (
          <div className="order-2 overflow-hidden bg-white dark:bg-slate-900" data-testid="anwesend-ohne-funktion">
            {/* Header analog zu einer Fahrzeug-Zeile, aber ohne Bild */}
            <div className="text-white px-2.5 py-1.5 flex items-center gap-2 bg-slate-600">
              <div className="w-8 h-6 rounded bg-white/10 flex items-center justify-center shrink-0">
                <UserPlus className="w-4 h-4" />
              </div>
              <h3 className="font-bold text-xs truncate flex-1">Anwesend ohne Funktion</h3>
              <div className="px-1.5 py-0.5 rounded text-[11px] font-bold shrink-0 bg-white/10 text-slate-100">
                {anwesend_ohne_funktion.length}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-px bg-border">
              {anwesend_ohne_funktion.map((p) => {
                const dgCfg = p.dienstgrad ? DIENSTGRAD_CONFIG[p.dienstgrad.toUpperCase()] : null;
                const quali = p.qualifikation;
                const nameParts = (p.mitarbeiter_name || '').split(' ');
                const vorname = nameParts.slice(0, -1).join(' ');
                const nachname = nameParts.slice(-1)[0] || '';
                const codeStyle =
                  p.code === 'ZBV'
                    ? { backgroundColor: '#475569', color: '#fff' }
                    : p.code === 'RW'
                    ? { backgroundColor: '#e11d48', color: '#fff' }
                    : { backgroundColor: '#7c3aed', color: '#fff' };
                return (
                  <div
                    key={`${p.mitarbeiter_id}-${p.code}`}
                    className="bg-white dark:bg-slate-900 flex items-stretch min-h-[44px]"
                    data-testid={`anwesend-${p.code}-${p.mitarbeiter_id}`}
                  >
                    <div
                      className="shrink-0 w-12 px-1.5 flex items-center justify-center text-center text-[10px] font-bold tracking-wide leading-tight border-r border-black/10"
                      style={codeStyle}
                      title={p.code_label}
                    >
                      {p.code}
                    </div>
                    <EmployeePhoto src={p.avatar_url} name={p.mitarbeiter_name} fullHeight />
                    <div className="flex-1 min-w-0 px-1.5 py-1 flex items-center gap-1.5">
                      {dgCfg?.image && (
                        <div className="w-4 h-10 shrink-0 flex items-center justify-center">
                          <img
                            src={dgCfg.image}
                            alt={p.dienstgrad}
                            title={dgCfg.label}
                            className="block max-w-none origin-center rotate-90"
                            style={{ width: '40px', height: '14px' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0 leading-tight">
                        {vorname && (
                          <div className="text-[10px] text-muted-foreground truncate">{vorname}</div>
                        )}
                        <div className="text-xs font-bold text-foreground truncate">{nachname || '—'}</div>
                      </div>
                      {quali && quali !== 'NONE' && (
                        <span
                          className={cn(
                            'shrink-0 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase',
                            quali === 'NotSan' ? 'bg-emerald-600 text-white' : ['RS', 'RS+', 'RSN'].includes(quali) ? 'bg-blue-600 text-white' : 'bg-slate-500 text-white'
                          )}
                          title={qualifikationLabels[quali] || quali}
                        >
                          {quali === 'NotSan' ? 'NFS' : quali}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        </div>
      </div>

      {sortedFahrzeuge.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            <Truck className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Keine Fahrzeuge für diese Abteilung konfiguriert</p>
          </CardContent>
        </Card>
      )}

      {/* Alte separate ZBV/WA/RW-Karte entfernt — jetzt integriert im Magnetboard-Container */}
    </div>
  );
}

export default MagnetboardView;
